import { GpsClear, GpsCopy, GpsRedo, GpsTutorialSetGreen, GpsTutorialSetTeebox, GpsUndo } from 'assets/images'
import { IntlShape } from 'react-intl'

export const LOCAL_STORAGE_GPS_EDITOR_TUTORIAL = 'gps-editor-tutorial'

export enum GPS_EDITOR_TUTORIAL {
  Start = 'start',
  SetTeeBox = 'set-teebox',
  HowToEdit = 'how-to-edit',
  SetGreen = 'set-green',
  Repeat = 'repeat',
  Toolbar = 'toolbar',
  Halfway = 'halfway',
  Finish = 'finish',
  Done = 'done',
}

export const saveGpsEditorTutorialPhase = (phase: GpsEditorTutorialPhase | null) => {
  if (phase === null) {
    window.localStorage.removeItem(LOCAL_STORAGE_GPS_EDITOR_TUTORIAL)
    return
  }
  window.localStorage.setItem(LOCAL_STORAGE_GPS_EDITOR_TUTORIAL, phase)
}

export const getGpsEditorTutorialPhase = (): GpsEditorTutorialPhase | null => {
  const phase = window.localStorage.getItem(LOCAL_STORAGE_GPS_EDITOR_TUTORIAL)
  return phase ? (phase as GpsEditorTutorialPhase) : null
}

export const getGpsEditorTutorialPhaseData = (phase: GpsEditorTutorialPhase): GpsEditorTutorialPhaseData => {
  return gpsEditorTutorialPhases.find((p) => p.phase === phase) as GpsEditorTutorialPhaseData
}

export const gpsEditorTutorialPhases: GpsEditorTutorialPhaseData[] = [
  {
    phase: GPS_EDITOR_TUTORIAL.Start,
    title: 'gpsEditorTutorial.startTitle',
    text: 'gpsEditorTutorial.startText',
    buttons: { primary: { label: 'gpsEditorTutorial.startButton', goTo: GPS_EDITOR_TUTORIAL.SetTeeBox } },
  },
  {
    phase: GPS_EDITOR_TUTORIAL.SetTeeBox,
    title: 'gpsEditorTutorial.setTeeBoxTitle',
    text: 'gpsEditorTutorial.setTeeBoxText',
    image: GpsTutorialSetTeebox,
    buttons: {
      primary: { label: 'gpsEditorTutorial.continueButton', goTo: GPS_EDITOR_TUTORIAL.HowToEdit },
      secondary: { label: 'gpsEditorTutorial.skipButton', goTo: GPS_EDITOR_TUTORIAL.Done },
    },
    progress: 25,
  },
  {
    phase: GPS_EDITOR_TUTORIAL.HowToEdit,
    title: 'gpsEditorTutorial.howToEditTitle',
    text: 'gpsEditorTutorial.howToEditText',
    buttons: {
      primary: { label: 'gpsEditorTutorial.continueButton', goTo: GPS_EDITOR_TUTORIAL.SetGreen },
      secondary: { label: 'gpsEditorTutorial.previousButton', goTo: GPS_EDITOR_TUTORIAL.SetTeeBox },
    },
    progress: 50,
  },
  {
    phase: GPS_EDITOR_TUTORIAL.SetGreen,
    title: 'gpsEditorTutorial.setGreenTitle',
    text: 'gpsEditorTutorial.setGreenText',
    image: GpsTutorialSetGreen,
    buttons: {
      primary: { label: 'gpsEditorTutorial.continueButton', goTo: GPS_EDITOR_TUTORIAL.Repeat },
      secondary: { label: 'gpsEditorTutorial.previousButton', goTo: GPS_EDITOR_TUTORIAL.HowToEdit },
    },
    progress: 75,
  },
  {
    phase: GPS_EDITOR_TUTORIAL.Repeat,
    title: 'gpsEditorTutorial.repeatTitle',
    text: 'gpsEditorTutorial.repeatText',
    buttons: {
      primary: { label: 'gpsEditorTutorial.continueButton', goTo: GPS_EDITOR_TUTORIAL.Toolbar },
      secondary: { label: 'gpsEditorTutorial.restartButton', goTo: GPS_EDITOR_TUTORIAL.Start },
    },
    progress: 100,
  },
  {
    phase: GPS_EDITOR_TUTORIAL.Toolbar,
    title: 'gpsEditorTutorial.toolbarTitle',
    text: '',
    buttons: {
      primary: { label: 'gpsEditorTutorial.gotItButton', goTo: null },
    },
  },
  {
    phase: GPS_EDITOR_TUTORIAL.Halfway,
    title: 'gpsEditorTutorial.halfwayTitle',
    text: 'gpsEditorTutorial.halfwayText',
    buttons: {
      primary: { label: 'gpsEditorTutorial.okButton', goTo: null },
    },
  },
  {
    phase: GPS_EDITOR_TUTORIAL.Finish,
    title: 'gpsEditorTutorial.finishTitle',
    text: 'gpsEditorTutorial.finishText',
    buttons: {
      primary: { label: 'gpsEditorTutorial.okButton', goTo: GPS_EDITOR_TUTORIAL.Done },
    },
  },
]

export const gpsEditorTutorialToolbarPhaseContent: GpsEditorTutorialToolbarPhaseContent[] = [
  { image: GpsUndo, text: 'gpsEditorTutorial.toolbarUndoText' },
  { image: GpsRedo, text: 'gpsEditorTutorial.toolbarRedoText' },
  { image: GpsClear, text: 'gpsEditorTutorial.toolbarClearText' },
  { image: GpsCopy, text: 'gpsEditorTutorial.toolbarCopyText' },
]

export const validateCourseHoles = (holes: CourseHole[], intl: IntlShape): CourseHoleValidationResponse => {
  const holeNumbers: number[] = []
  const hcpMen: number[] = []
  const hcpWomen: number[] = []
  const holeValidationErrorsArray: string[] = []

  holes.map((ch: CourseHole) => {
    // Hole numbers
    if (ch.holeNumber < 1 || ch.holeNumber > 18) {
      const invalidHoleNumber = intl.formatMessage({ id: 'strings.invalidHoleNumber' })
      holeValidationErrorsArray.push(invalidHoleNumber)
    } else {
      holeNumbers.push(ch.holeNumber)
    }
    // HCP Men
    if (ch.hcpMen < 1 || ch.hcpMen > 18) {
      const invalidHcpMenValueForHole = intl.formatMessage({ id: 'strings.invalidHcpMenValueForHole' })
      holeValidationErrorsArray.push(`${invalidHcpMenValueForHole} ${ch.holeNumber}`)
    } else {
      hcpMen.push(ch.hcpMen)
    }
    // HCP Women
    if (ch.hcpWomen < 1 || ch.hcpWomen > 18) {
      const invalidHcpLadiesValueForHole = intl.formatMessage({ id: 'strings.invalidHcpLadiesValueForHole' })
      holeValidationErrorsArray.push(`${invalidHcpLadiesValueForHole} ${ch.holeNumber}`)
    } else {
      hcpWomen.push(ch.hcpWomen)
    }
    // Par
    if (ch.par < 1 || ch.par > 9) {
      const invalidParValueForHole = intl.formatMessage({ id: 'strings.invalidParValueForHole' })
      holeValidationErrorsArray.push(`${invalidParValueForHole} ${ch.holeNumber}`)
    }
    return ch
  })

  let noDuplicates = true
  const duplicateHoles = holeNumbers.filter((value, index, array) => array.indexOf(value) !== index)
  if (duplicateHoles.length > 0) {
    noDuplicates = false
  }
  const duplicatesHcpMen = hcpMen.filter((value, index, array) => array.indexOf(value) !== index)
  if (duplicatesHcpMen.length > 0) {
    noDuplicates = false
  }
  const duplicatesHcpWomen = hcpWomen.filter((value, index, array) => array.indexOf(value) !== index)
  if (duplicatesHcpWomen.length > 0) {
    noDuplicates = false
  }

  if (!noDuplicates) {
    const duplicateValues = intl.formatMessage({ id: 'strings.duplicateValuesInHoleData' })
    holeValidationErrorsArray.push(duplicateValues)
  }

  return {
    noDuplicates,
    holeValidationErrorsArray,
  }
}

export const validateCourseTeeBoxes = (teeBoxes: CourseTeeBox[], intl: IntlShape): CourseTeeBoxValidationResponse => {
  const teeBoxValidationErrorsArray: string[] = []
  const minCrValue = 10.0
  const minSlopeValue = 55.0

  if (teeBoxes.length === 0) {
    const addAtLeastOneTeeBox = intl.formatMessage({ id: 'strings.addAtLeastOneTeeBox' })
    teeBoxValidationErrorsArray.push(addAtLeastOneTeeBox)
  } else {
    teeBoxes.forEach((tb: CourseTeeBox) => {
      if (!tb.name) {
        const teeBoxMissingName = intl.formatMessage({ id: 'strings.teeBoxMissingName' })
        teeBoxValidationErrorsArray.push(teeBoxMissingName)
      }

      if (!tb.crMen || !tb.crWomen) {
        const teeBoxMissingCrData = intl.formatMessage({ id: 'strings.teeBoxMissingCrData' })
        teeBoxValidationErrorsArray.push(teeBoxMissingCrData)
      }

      if (!tb.slopeMen || !tb.slopeWomen) {
        const teeBoxMissingSlopeData = intl.formatMessage({ id: 'strings.teeBoxMissingSlopeData' })
        teeBoxValidationErrorsArray.push(teeBoxMissingSlopeData)
      }

      if ((tb.crMen > 0 && tb.crMen < minCrValue) || (tb.crWomen > 0 && tb.crWomen < minCrValue)) {
        const crValueTooLow = intl.formatMessage({ id: 'strings.teeBoxCrValueTooLow' })
        teeBoxValidationErrorsArray.push(crValueTooLow)
      }

      if ((tb.slopeMen > 0 && tb.slopeMen < minSlopeValue) || (tb.slopeWomen > 0 && tb.slopeWomen < minSlopeValue)) {
        const slopeValueTooLow = intl.formatMessage({ id: 'strings.slopeValueTooLow' })
        teeBoxValidationErrorsArray.push(slopeValueTooLow)
      }

      if ([tb.crMen, tb.slopeMen, tb.crWomen, tb.slopeWomen].every((value: number) => value === -1)) {
        const teeBoxWithoutValues = intl.formatMessage({ id: 'strings.teeBoxWithoutValues' })
        teeBoxValidationErrorsArray.push(teeBoxWithoutValues)
      }
    })
  }
  return {
    teeBoxValidationErrorsArray,
  }
}
